import { useContext } from "react";
import { Portal, Tooltip } from "@ark-ui/react";

import { Icon } from "@/components/icon/icon";
import { useTranslation } from "@/lib/i18n";
import { RefreshContext } from "@/features/dashboard/features/refresh-timer/context";

import styles from "./refresh-control.module.css";

/**
 * Manual refresh button for the CMS toolbar. Triggers the host's RefreshContext
 * (`refreshNow`), which re-runs every registered widget refresh action (map
 * tiles, series, ...). Renders only when a RefreshContext is present and is
 * disabled while a refresh is in flight.
 *
 * The tooltip uses ark-ui's portalled Tooltip (not MUI) so its positioner sits
 * in the same stacking tier as the toolbar popovers and stays above the widget
 * editor dialog. Copy makes clear the CMS preview only refreshes manually
 * (previews do not auto-refresh on a timer).
 */
export function RefreshControl() {
  const { t } = useTranslation();
  const refreshTimer = useContext(RefreshContext);

  if (!refreshTimer) return null;

  const { refreshNow, isRefreshing } = refreshTimer;

  return (
    <Tooltip.Root lazyMount openDelay={200} closeDelay={0}>
      <Tooltip.Trigger asChild>
        {/* span wrapper so the tooltip still shows while the button is disabled */}
        <span className={styles.triggerWrap}>
          <button
            type="button"
            className={styles.button}
            onClick={refreshNow}
            disabled={isRefreshing}
            aria-label={t("dashboard.refreshTimer.refresh")}
          >
            <Icon name="refresh-ccw" size={16} />
          </button>
        </span>
      </Tooltip.Trigger>
      <Portal>
        <Tooltip.Positioner className={styles.tooltipPositioner}>
          <Tooltip.Content className={styles.tooltipContent}>
            {isRefreshing
              ? t("dashboard.refreshTimer.refreshing")
              : t("dashboard.refreshTimer.manualOnlyTooltip")}
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  );
}
